package com.example.borshi.web.account

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.example.borshi.web.auth.UserProfile
import com.example.borshi.web.firebase.DailyDelivery
import com.example.borshi.web.firebase.FirebaseService
import com.example.borshi.web.firebase.Vacation
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.backgroundColor
import org.jetbrains.compose.web.css.border
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.color
import org.jetbrains.compose.web.css.cursor
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.fontWeight
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.css.margin
import org.jetbrains.compose.web.css.marginBottom
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.textAlign
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
fun SubscriptionScheduleSection(user: UserProfile) {
    var deliveries by remember { mutableStateOf<List<DailyDelivery>>(emptyList()) }
    var vacations by remember { mutableStateOf<List<Vacation>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    var showVacationForm by remember { mutableStateOf(false) }
    var vacationStart by remember { mutableStateOf("") }
    var vacationEnd by remember { mutableStateOf("") }
    val scope = rememberCoroutineScope()

    fun loadData() {
        scope.launch {
            isLoading = true
            errorMessage = null
            try {
                deliveries = FirebaseService.getDailyDeliveries()
                vacations = FirebaseService.getVacations()
            } catch (e: Throwable) {
                errorMessage = e.message ?: "Failed to load schedule"
            } finally {
                isLoading = false
            }
        }
    }

    LaunchedEffect(user.userId) {
        loadData()
    }

    Div({
        style {
            backgroundColor(Color.white)
            borderRadius(16.px)
            padding(24.px)
            property("box-shadow", "0 4px 12px rgba(0,0,0,0.08)")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(16.px)
        }
    }) {
        HeaderRow(onRefresh = { loadData() }, onAddVacation = { showVacationForm = true })

        if (showVacationForm) {
            VacationForm(
                startDate = vacationStart,
                endDate = vacationEnd,
                onStartChange = { vacationStart = it },
                onEndChange = { vacationEnd = it },
                onSubmit = {
                    if (vacationStart.isNotBlank() && vacationEnd.isNotBlank()) {
                        scope.launch {
                            try {
                                FirebaseService.addVacation(vacationStart, vacationEnd)
                                showVacationForm = false
                                vacationStart = ""
                                vacationEnd = ""
                                loadData()
                            } catch (e: Throwable) {
                                errorMessage = e.message ?: "Failed to add vacation"
                            }
                        }
                    }
                },
                onCancel = {
                    showVacationForm = false
                    vacationStart = ""
                    vacationEnd = ""
                }
            )
        }

        if (vacations.isNotEmpty()) {
            VacationsList(vacations, onDelete = { id ->
                scope.launch {
                    try {
                        FirebaseService.deleteVacation(id)
                        loadData()
                    } catch (e: Throwable) {
                        errorMessage = e.message ?: "Failed to delete vacation"
                    }
                }
            })
        }

        when {
            isLoading -> LoadingState()
            errorMessage != null -> ErrorState(errorMessage!!)
            deliveries.isEmpty() -> EmptyScheduleState()
            else -> DeliveriesList(deliveries)
        }
    }
}

@Composable
private fun HeaderRow(onRefresh: () -> Unit, onAddVacation: () -> Unit) {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
        }
    }) {
        H2({ style { margin(0.px); fontSize(22.px); fontWeight("700") } }) { Text("My Subscription Schedule") }
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(8.px)
            }
        }) {
            Button({
                style {
                    backgroundColor(Color("#F1F5F9"))
                    color(Color("#0F172A"))
                    border(0.px)
                    padding(8.px, 12.px)
                    borderRadius(8.px)
                    cursor("pointer")
                    fontSize(13.px)
                }
                onClick { onRefresh() }
            }) { Text("Refresh") }

            Button({
                style {
                    backgroundColor(Color("#D11243"))
                    color(Color.white)
                    border(0.px)
                    padding(8.px, 14.px)
                    borderRadius(8.px)
                    cursor("pointer")
                    fontSize(13.px)
                    fontWeight("600")
                }
                onClick { onAddVacation() }
            }) { Text("Add Vacation") }
        }
    }
}

@Composable
private fun VacationForm(
    startDate: String,
    endDate: String,
    onStartChange: (String) -> Unit,
    onEndChange: (String) -> Unit,
    onSubmit: () -> Unit,
    onCancel: () -> Unit
) {
    Div({
        style {
            backgroundColor(Color("#FFF4F8"))
            borderRadius(12.px)
            padding(16.px)
            border(1.px, LineStyle.Solid, Color("#FFD1DC"))
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(12.px)
        }
    }) {
        P({ style { margin(0.px); color(Color("#7A1232")); fontWeight("600") } }) {
            Text("Pause Deliveries")
        }
        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "1fr 1fr")
                gap(12.px)
            }
        }) {
            Input(type = org.jetbrains.compose.web.attributes.InputType.Date) {
                value(startDate)
                onInput { onStartChange(it.value) }
                style {
                    padding(8.px)
                    borderRadius(8.px)
                    border(1.px, LineStyle.Solid, Color("#E2E8F0"))
                }
            }
            Input(type = org.jetbrains.compose.web.attributes.InputType.Date) {
                value(endDate)
                onInput { onEndChange(it.value) }
                style {
                    padding(8.px)
                    borderRadius(8.px)
                    border(1.px, LineStyle.Solid, Color("#E2E8F0"))
                }
            }
        }
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(8.px)
                justifyContent(JustifyContent.FlexEnd)
            }
        }) {
            Button({
                style {
                    backgroundColor(Color.transparent)
                    color(Color("#475569"))
                    border(0.px)
                    padding(8.px, 12.px)
                    cursor("pointer")
                }
                onClick { onCancel() }
            }) { Text("Cancel") }

            Button({
                style {
                    backgroundColor(Color("#D11243"))
                    color(Color.white)
                    border(0.px)
                    padding(8.px, 16.px)
                    borderRadius(8.px)
                    cursor("pointer")
                    fontWeight("600")
                }
                onClick { onSubmit() }
            }) { Text("Save") }
        }
    }
}

@Composable
private fun LoadingState() {
    Div({
        style {
            padding(24.px)
            textAlign("center")
            color(Color("#6B7280"))
        }
    }) { Text("Loading upcoming deliveries…") }
}

@Composable
private fun ErrorState(message: String) {
    Div({
        style {
            padding(16.px)
            backgroundColor(Color("#FEF2F2"))
            borderRadius(12.px)
            border(1.px, LineStyle.Solid, Color("#FCA5A5"))
            color(Color("#B91C1C"))
        }
    }) {
        Text(message)
    }
}

@Composable
private fun EmptyScheduleState() {
    Div({
        style {
            padding(24.px)
            textAlign("center")
            color(Color("#6B7280"))
        }
    }) {
        Text("You don’t have any upcoming deliveries. Start a subscription to see schedule here.")
    }
}

@Composable
private fun DeliveriesList(deliveries: List<DailyDelivery>) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(12.px)
        }
    }) {
        deliveries.forEach { delivery ->
            DeliveryCard(delivery)
        }
    }
}

@Composable
private fun DeliveryCard(delivery: DailyDelivery) {
    Div({
        style {
            border(1.px, LineStyle.Solid, Color("#E2E8F0"))
            borderRadius(12.px)
            padding(16.px)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
        }
    }) {
        Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(4.px) } }) {
            Span({ style { fontWeight("600"); fontSize(15.px) } }) { Text(delivery.productName) }
            Span({ style { color(Color("#64748B")); fontSize(13.px) } }) { Text("Qty: ${delivery.quantity}") }
        }
        Div({ style { textAlign("right") } }) {
            Span({ style { fontWeight("600"); fontSize(14.px) } }) { Text(delivery.date) }
            Span({
                style {
                    color(
                        when (delivery.status.uppercase()) {
                            "SCHEDULED" -> Color("#15803D")
                            "PAUSED" -> Color("#B45309")
                            else -> Color("#1D4ED8")
                        }
                    )
                    fontSize(12.px)
                    fontWeight("600")
                }
            }) { Text(delivery.status) }
        }
    }
}

@Composable
private fun VacationsList(vacations: List<Vacation>, onDelete: (String) -> Unit) {
    Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(12.px); marginBottom(24.px) } }) {
        P({ style { fontSize(18.px); fontWeight("700"); margin(0.px, 0.px, 12.px, 0.px) } }) { Text("Upcoming Vacations") }
        vacations.forEach { vacation ->
            VacationCard(vacation, onDelete)
        }
    }
}

@Composable
private fun VacationCard(vacation: Vacation, onDelete: (String) -> Unit) {
    Div({
        style {
            backgroundColor(Color("#F0FDFA"))
            border(1.px, LineStyle.Solid, Color("#CCFBF1"))
            borderRadius(12.px)
            padding(16.px)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
        }
    }) {
        Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(4.px) } }) {
            Span({ style { fontWeight("600"); fontSize(15.px); color(Color("#0F766E")) } }) {
                Text("🌴 Vacation Mode")
            }
            Span({ style { color(Color("#5E6E6C")); fontSize(14.px) } }) {
                Text("${vacation.startDate.toLocaleDateString()} - ${vacation.endDate.toLocaleDateString()}")
            }
        }
        Button({
            style {
                backgroundColor(Color.white)
                color(Color("#EF4444"))
                border(1.px, LineStyle.Solid, Color("#FECACA"))
                padding(6.px, 12.px)
                borderRadius(6.px)
                cursor("pointer")
                fontSize(12.px)
                fontWeight("600")
            }
            onClick { onDelete(vacation.id) }
        }) { Text("Cancel") }
    }
}
